import { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { statusMeta, type Match } from '@/lib/site';

interface FeaturedCarouselProps {
  items: Match[];
}

export default function FeaturedCarousel({ items }: FeaturedCarouselProps) {
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const touchStart = useRef<number | null>(null);
  const count = items.length;

  const goTo = useCallback(
    (next: number) => {
      if (count === 0) return;
      setIndex(((next % count) + count) % count);
    },
    [count],
  );

  useEffect(() => {
    if (paused || count <= 1) return;
    const timer = window.setInterval(() => {
      setIndex((prev) => (prev + 1) % count);
    }, 6000);
    return () => window.clearInterval(timer);
  }, [paused, count]);

  if (count === 0) return null;

  const onTouchStart = (event: React.TouchEvent) => {
    touchStart.current = event.touches[0].clientX;
  };

  const onTouchEnd = (event: React.TouchEvent) => {
    if (touchStart.current === null) return;
    const delta = event.changedTouches[0].clientX - touchStart.current;
    if (Math.abs(delta) > 48) {
      goTo(delta > 0 ? index - 1 : index + 1);
    }
    touchStart.current = null;
  };

  return (
    <section
      aria-label="焦点直播"
      className="relative w-full overflow-hidden rounded-lg bg-primary-950"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
    >
      <div className="relative h-[440px] sm:h-[460px] lg:h-[520px] w-full">
        {items.map((match, i) => (
          <Link
            key={match.id}
            to={`/detail/${match.id}`}
            className={`absolute inset-0 transition-opacity duration-700 ease-out ${
              i === index ? 'opacity-100 z-10' : 'opacity-0 z-0 pointer-events-none'
            }`}
            aria-hidden={i !== index}
            tabIndex={i === index ? 0 : -1}
          >
            <img
              src={match.cover}
              alt={`${match.home.name} 对阵 ${match.away.name} 赛事直播封面`}
              title={`${match.home.name} VS ${match.away.name} 篮球直播`}
              className="absolute inset-0 w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-primary-950/95 via-primary-950/75 to-primary-950/25" />
            <div className="absolute inset-0 bg-gradient-to-t from-primary-950/80 via-transparent to-primary-950/30" />

            <div className="relative z-10 flex h-full w-full flex-col justify-end px-5 pb-8 pt-16 md:px-10 md:pb-10 lg:px-12">
              <div className="flex flex-wrap items-center gap-2 mb-3">
                <span className="inline-flex items-center gap-1.5 rounded-full bg-accent-500 px-2.5 py-1 text-[11px] font-medium text-background-50">
                  {match.status === 'live' && <span className="w-1.5 h-1.5 rounded-full bg-background-50 animate-pulse-dot" />}
                  {statusMeta[match.status].label}
                </span>
                <span className="rounded-full bg-background-50/15 px-2.5 py-1 text-[11px] text-background-100 backdrop-blur-sm">
                  {match.leagueLabel} · {match.round.split('·')[1]?.trim() || '焦点赛事'}
                </span>
                <span className="hidden sm:inline-flex items-center gap-1 rounded-full bg-background-50/15 px-2.5 py-1 text-[11px] text-background-100 backdrop-blur-sm">
                  <i className="ri-eye-line" />
                  {match.views}
                </span>
              </div>

              <p className="font-heading text-[11px] tracking-[0.3em] text-accent-300 mb-2">
                FEATURED MATCH
              </p>

              <h2 className="font-heading text-2xl md:text-4xl lg:text-[2.6rem] leading-tight text-background-50 max-w-2xl">
                {match.home.name}
                <span className="mx-2 text-accent-400 text-xl md:text-2xl">VS</span>
                {match.away.name}
              </h2>

              <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1.5 text-sm text-background-200">
                <span className="inline-flex items-center gap-1.5">
                  <i className="ri-calendar-line" />
                  {match.timeLabel}
                </span>
                <span className="inline-flex items-center gap-1.5">
                  <i className="ri-map-pin-line" />
                  {match.venue}
                </span>
              </div>

              <p className="hidden md:block mt-3 max-w-xl text-sm leading-relaxed text-background-300 line-clamp-2">
                {match.summary}
              </p>

              <div className="mt-5 flex items-center gap-3">
                <span className="inline-flex items-center gap-2 rounded-md bg-accent-500 px-4 py-2.5 text-sm font-medium text-background-50">
                  {match.status === 'live' ? '进入直播间' : '查看赛事详情'}
                  <i className="ri-arrow-right-line" />
                </span>
                <span className="hidden sm:inline-flex items-center gap-2 rounded-md border border-background-50/30 px-4 py-2.5 text-sm text-background-100">
                  <i className="ri-line-chart-line" />
                  实时赛况
                </span>
              </div>
            </div>
          </Link>
        ))}
      </div>

      <div className="absolute bottom-3 right-4 md:bottom-4 md:right-6 z-20 flex items-center gap-2">
        <button
          type="button"
          onClick={() => goTo(index - 1)}
          aria-label="上一场赛事"
          className="w-8 h-8 md:w-9 md:h-9 rounded-full bg-background-50/15 backdrop-blur-sm text-background-50 flex items-center justify-center hover:bg-background-50/25 transition-colors cursor-pointer"
        >
          <i className="ri-arrow-left-s-line" />
        </button>
        <button
          type="button"
          onClick={() => goTo(index + 1)}
          aria-label="下一场赛事"
          className="w-8 h-8 md:w-9 md:h-9 rounded-full bg-background-50/15 backdrop-blur-sm text-background-50 flex items-center justify-center hover:bg-background-50/25 transition-colors cursor-pointer"
        >
          <i className="ri-arrow-right-s-line" />
        </button>
      </div>

      <div className="absolute bottom-3 left-5 md:bottom-4 md:left-12 z-20 flex items-center gap-1.5">
        {items.map((match, i) => (
          <button
            key={match.id}
            type="button"
            onClick={() => goTo(i)}
            aria-label={`切换到第 ${i + 1} 场赛事`}
            className={`h-1 rounded-full transition-all cursor-pointer ${
              i === index ? 'w-8 bg-accent-400' : 'w-3 bg-background-50/40 hover:bg-background-50/70'
            }`}
          />
        ))}
      </div>
    </section>
  );
}